import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { DOCS_BASE_PATH, DOCS_DEFAULT_PATH } from "../../pages/docs/config/docConstants";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  TestTube2,
  BarChart3,
  ArrowUpRight,
  ChevronDown,
} from "lucide-react";

const productItems = [
  {
    icon: TestTube2,
    name: "QA Path",
    desc: "AI-powered test automation — write, execute, and manage test cases effortlessly.",
    link: "/login",
  },
  {
    icon: BarChart3,
    name: "TestLens",
    desc: "Turn thousands of test results into clear, actionable reports with AI analysis.",
    link: "https://testlens.qa-path.com",
  },
];

const navItems = [
  { label: "Services", id: "services" },
  { label: "Pricing", id: "pricing" },
  { label: "Contact Us", id: "contact" },
];

const isDocsPath = (pathname) =>
  pathname === DOCS_BASE_PATH || pathname.startsWith(`${DOCS_BASE_PATH}/`);

const Navbar = ({ onNavClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isDocsPage = isDocsPath(location.pathname);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goToSignup = () => navigate("/signup");
  const goToLogin = () => navigate("/login");

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass-strong shadow-md border-b border-border/50"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-[72px] px-4">
        <button
          type="button"
          className="flex items-center gap-3"
          onClick={() => navigate(isDocsPage ? DOCS_DEFAULT_PATH : "/")}
        >
          <img
            src="/image.png"
            alt="QA Path"
            className="h-9 w-9 object-contain"
          />
          <span className="font-display font-semibold text-lg text-foreground tracking-tight">
            QA Path
          </span>
        </button>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-1">
          {/* Products dropdown */}
          <div 
            ref={dropdownRef} 
            className="relative"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              type="button"
              className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg transition-colors duration-200 ${
                productsOpen
                  ? "text-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
              }`}
            >
              Products
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 ${
                  productsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] z-50 rounded-2xl border border-border bg-card shadow-xl p-2"
                >
                  {productItems.map((product) => (
                    <a
                      key={product.name}
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setProductsOpen(false)}
                      className="flex items-start gap-3.5 p-3.5 rounded-xl hover:bg-secondary/50 transition-colors duration-200 group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-primary/15 transition-colors duration-300">
                        <product.icon size={17} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-foreground tracking-tight">
                            {product.name}
                          </span>
                          <ArrowUpRight
                            size={11}
                            className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                          {product.desc}
                        </p>
                      </div>
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {!isDocsPage && navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => onNavClick && onNavClick(item.id)}
              className="text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors duration-200 px-4 py-2 rounded-lg"
            >
              {item.label}
            </button>
          ))}

          {isDocsPage ? (
            <NavLink
              to={DOCS_DEFAULT_PATH}
              className={({ isActive }) =>
                `text-sm transition-colors duration-200 px-4 py-2 rounded-lg ${
                  isActive
                    ? "text-primary font-medium bg-primary/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
                }`
              }
            >
              Docs
            </NavLink>
          ) : (
            <Link
              to={DOCS_DEFAULT_PATH}
              className="text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors duration-200 px-4 py-2 rounded-lg"
            >
              Docs
            </Link>
          )}

          <div className="ml-2 pl-4 border-l border-border/40">
            <button
              type="button"
              onClick={goToSignup}
              className="text-sm font-semibold bg-primary text-primary-foreground px-6 py-2.5 rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="md:hidden text-foreground p-2 rounded-lg hover:bg-secondary/30 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-t border-border/40"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col gap-1">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-2 px-3">
                Products
              </span>
              {productItems.map((product) => (
                <a
                  key={product.name}
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3.5 p-3 rounded-xl hover:bg-secondary/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center">
                    <product.icon size={16} className="text-primary" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground">
                      {product.name}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {product.desc}
                    </p>
                  </div>
                </a>
              ))}

              <div className="h-px bg-border/30 my-3" />

              {!isDocsPage && navItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="text-left text-muted-foreground hover:text-foreground transition-colors py-2.5 px-3 rounded-lg hover:bg-secondary/30"
                  onClick={() => {
                    setMobileOpen(false);
                    onNavClick && onNavClick(item.id);
                  }}
                >
                  {item.label}
                </button>
              ))}

              <Link
                to={DOCS_DEFAULT_PATH}
                onClick={() => setMobileOpen(false)}
                className={`text-left transition-colors py-2.5 px-3 rounded-lg text-sm block ${
                  isDocsPage
                    ? "text-primary font-medium bg-primary/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
                }`}
              >
                Docs
              </Link>

              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  goToLogin();
                }}
                className="mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2.5 px-3 rounded-lg hover:bg-secondary/30 text-left"
              >
                Login
              </button>

              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    goToSignup();
                  }}
                  className="block w-full text-sm font-semibold bg-primary text-primary-foreground px-5 py-3.5 rounded-xl text-center glow-sm"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;

